import i18next from 'i18next';
import {getFormValues} from 'redux-form';
import {List, Map} from 'immutable';
import moment from 'moment-timezone';

import createSelector from 'web-app/util/custom-create-selector';
import {showAlert} from 'web-app/react/dialog';
import * as FileHelpers from 'web-app/react/entities/files/helpers';
import {deserialize} from 'web-app/react/components/file-upload/file-record';
import {isUploading} from 'web-app/react/components/file-upload/selectors';
import {receiverIds, selectedReceivers} from 'web-app/react/inbox/new-inbox-conversation/selectors';
import LocalStorageService from 'web-app/services/local-storage';
import {track} from 'web-app/util/famlytics';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as SessionSelectors from 'web-app/react/session/account/selectors';
import {receiversWithParticipants} from 'web-app/react/inbox/new-inbox-conversation/selectors';
import {showSmsConfirmationDialog} from 'web-app/react/sms-notifications/show-sms-confirmation-dialog';

import * as Actions from './actions';
import * as Constants from './constants';
import * as RootConstants from '../constants';
import {extractDataFromParticipantsStrings} from '../new-inbox-conversation/helpers';

const formValues = state => getFormValues(Constants.FORM)(state);
const rawFiles = state => state.fileUpload.files.get(Constants.FORM);
const messagesToForward = state => state.inbox.inboxMain.messagesToForward;
const isParent = state => SessionSelectors.getIsParent(state);
const getConversation = state => state.inbox.conversation.conversation;

export const getInstitutionSetId = createSelector(
    getConversation,
    receiversWithParticipants,
    (conversation, receiversWithParticipants) => {
        const institutionIdInSelectedConversation = conversation.get('institutionSetId');
        if (!institutionIdInSelectedConversation) {
            const participantsData = extractDataFromParticipantsStrings(
                receiversWithParticipants.flatMap(reciever => reciever.participants),
            );
            const siteSetIds = participantsData.map(([, , siteId]) => siteId);

            // Ensure `siteSetIds` are unique
            return Array.from(new Set(siteSetIds));
        }

        return [institutionIdInSelectedConversation];
    },
);

export const hasOnlyStaffSelected = createSelector(
    getConversation,
    receiversWithParticipants,
    (conversation, receiversWithParticipants) => {
        const institutionIdInSelectedConversation = conversation.get('institutionSetId');
        if (!institutionIdInSelectedConversation) {
            const loginIdAndContext = extractDataFromParticipantsStrings(
                receiversWithParticipants.flatMap(reciever => reciever.participants),
            );

            return loginIdAndContext.every(([, context]) => context === 'Professional');
        }

        return extractDataFromParticipantsStrings(
            conversation.get('participants').map(participant => participant.id) ?? [],
        ).every(([, context]) => context === 'Professional');
    },
);

function sendMessage(
    isNewConversation,
    isParent,
    receivers,
    body,
    imageIds,
    fileIds,
    shouldArchive,
    conversationId,
    sendAsSms,
) {
    if (isNewConversation) {
        // Send tracking information if not parent
        if (!isParent) {
            track(TrackingEvents.MESSAGING_MESSAGE_SENT, {
                meta: {
                    // The sender is also included in `receivers`.
                    participants: receivers.size - 1,
                },
            });
        }
        Actions.sendNew.dispatch(body, receivers, imageIds, fileIds, shouldArchive, sendAsSms);
    } else {
        Actions.reply.dispatch(body, conversationId, imageIds, fileIds, shouldArchive, sendAsSms);
    }
}

export const getOnSubmit = createSelector(
    rawFiles,
    isParent,
    isUploading,
    receiverIds,
    (_, props) => props.conversationId,
    (_, props) => props.resetSmsField,
    (_, props) => props.pricePerMessage,
    (files, isParent, uploading, receivers, conversationId, resetSmsField, pricePerMessage) => {
        const {imageIds, fileIds} = FileHelpers.getFileArrays(files);

        const onSubmit = (data, options) => {
            const shouldArchive = Boolean(options?.shouldArchive);

            if (uploading) {
                showAlert({
                    title: i18next.t('messages.waitForUpload'),
                });
                return;
            }

            const isNewConversation =
                conversationId === RootConstants.NEW_CONVERSATION_ID ||
                conversationId === RootConstants.FORWARD_CONVERSATION_ID;

            if (data.sendAsSms) {
                showSmsConfirmationDialog({
                    predicate: true,
                    scope: 'messages',
                    priceWarning: pricePerMessage ?? '',
                    onCancel: resetSmsField,
                    onSubmit: () => {
                        sendMessage(
                            isNewConversation,
                            isParent,
                            receivers,
                            data.body,
                            imageIds,
                            fileIds,
                            shouldArchive,
                            conversationId,
                            data.sendAsSms,
                        );
                        track(TrackingEvents.SMS_SENT_FROM_MESSAGE);
                    },
                }).then(() => {
                    resetSmsField();
                });

                return;
            }

            sendMessage(
                isNewConversation,
                isParent,
                receivers,
                data.body,
                imageIds,
                fileIds,
                shouldArchive,
                conversationId,
            );
        };

        return onSubmit;
    },
);

export const hasSelectedReceivers = createSelector(
    receiverIds,
    ids => ids.size > 1, // sender's id is also in the list
);

export const makeValuesFromDraft = () =>
    createSelector(
        (state, props) => props.conversationId,
        conversationId => {
            const draft = LocalStorageService.getForUserContextEncrypted(conversationId, false);
            if (draft) {
                const filesAreOutOfDate =
                    draft && draft.files && draft.timestamp && !moment().subtract(2, 'hour').isBefore(draft.timestamp);
                return {
                    ...draft,
                    timestamp: draft.files ? draft.timestamp : moment().format(),
                    selectedReceivers: draft.selectedReceivers ? Map(draft.selectedReceivers).map(List) : undefined,
                    files:
                        !filesAreOutOfDate && draft.files && draft.files.length > 0
                            ? List(draft.files).map(deserialize)
                            : undefined,
                    sendAsSms: false,
                };
            }
            return undefined;
        },
    );

export const makeValuesToDraft = () =>
    createSelector(
        formValues,
        rawFiles,
        selectedReceivers,
        makeValuesFromDraft(),
        (formValues, files, selectedReceivers, valuesFromDraft) => {
            const formObjectValues = formValues ? Object.values(formValues) : undefined;
            const filesAreOutOfDate =
                files &&
                valuesFromDraft &&
                valuesFromDraft.timestamp &&
                !moment().subtract(2, 'hour').isBefore(valuesFromDraft.timestamp);
            return {
                timestamp: valuesFromDraft && valuesFromDraft.timestamp ? valuesFromDraft.timestamp : moment().format(),
                formValues:
                    formObjectValues && formObjectValues.length > 0 && formObjectValues.some(val => Boolean(val))
                        ? formValues
                        : undefined,
                files: !filesAreOutOfDate && files && files.size > 0 ? files.toJS() : undefined,
                selectedReceivers:
                    selectedReceivers && selectedReceivers.size > 0 ? selectedReceivers.toJS() : undefined,
            };
        },
    );

export const makeInitialValues = () =>
    createSelector(makeValuesFromDraft(), messagesToForward, (valuesFromDraft, messagesToForward) => {
        if (hasValue(messagesToForward) && messagesToForward.length > 0) {
            return {
                body: messagesToForward.reduce((composedMessageBody, currentMessageBody) => {
                    if (composedMessageBody === '') {
                        return composedMessageBody.concat(currentMessageBody);
                    }
                    return composedMessageBody.concat('\n\n', currentMessageBody);
                }),
            };
        }
        if (valuesFromDraft && valuesFromDraft.formValues) {
            return {...valuesFromDraft.formValues, sendAsSms: false};
        }
        return undefined;
    });

export const makeHasMessagesToBeForwarded = () =>
    createSelector(messagesToForward, messagesToForward => hasValue(messagesToForward) && messagesToForward.length > 0);

export const makeHasDraftedMessage = () =>
    createSelector(
        makeInitialValues(),
        initialValues => initialValues && Object.values(initialValues).some(val => val !== undefined),
    );
